"use client";

import { useEffect, useRef } from "react";

/**
 * useOnChangeMap
 *
 * Watches an object of reactive values and triggers a callback **only when
 * one or more keys change** between renders.
 *
 * Provides both granular and summary information about what changed.
 *
 * @template T
 * @param {T} values - An object of reactive values to watch.
 * @param {(payload: {
 *   prev: T;
 *   next: T;
 *   changedKeys: Array<keyof T & string>;
 *   changedKey: (keyof T & string) | null;
 *   changedCount: number;
 *   isSingleChange: boolean;
 * }) => void} callback - Function called whenever one or more values change.
 *
 * @example
 * // Single key tracking
 * useOnChangeMap(
 *   { chainId },
 *   ({ changedKey, next }) => {
 *     if (changedKey === 'chainId') {
 *       console.log('chainId →', next.chainId)
 *     }
 *   },
 * )
 *
 * // Multi-key tracking
 * useOnChangeMap(
 *   { chainId, account, isConnected },
 *   ({ changedKeys, isSingleChange }) => {
 *     if (isSingleChange) {
 *       console.log(`${changedKeys[0]} changed`)
 *     } else {
 *       console.log('multiple values changed:', changedKeys)
 *     }
 *   },
 * )
 */
export function useOnChangeMap<T extends Record<string, unknown>>(
  values: T,
  callback: (payload: {
    prev: T;
    next: T;
    changedKeys: Array<keyof T & string>;
    changedKey: (keyof T & string) | null;
    changedCount: number;
    isSingleChange: boolean;
  }) => void,
) {
  const prevRef = useRef(values);
  const isFirstRenderRef = useRef(true);

  useEffect(
    function handleMapChangeDetection() {
      const prevVal = prevRef.current;
      const nextVal = values;

      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        prevRef.current = nextVal;
        return;
      }

      const changedKeys: Array<keyof T & string> = [];

      for (const key in nextVal) {
        if (Object.hasOwn(nextVal, key)) {
          if (prevVal[key] !== nextVal[key]) {
            changedKeys.push(key);
          }
        }
      }

      if (changedKeys.length === 0) return;

      const changedCount = changedKeys.length;
      const isSingleChange = changedCount === 1;
      const changedKey = isSingleChange ? changedKeys[0] : null;

      callback({
        prev: prevVal,
        next: nextVal,
        changedKeys,
        changedKey,
        changedCount,
        isSingleChange,
      });

      prevRef.current = nextVal;
    },
    [values, callback],
  );
}
